import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';

export interface DashboardLoadingStep {
    title: string;
    activeText: string;
    completedText: string;
    iconPath: string;
}

@Component({
    selector: 'app-dashboard-loader',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './dashboard-loader.html',
    styleUrls: ['./dashboard-loader.css']
})
export class DashboardLoader implements OnInit, OnChanges, OnDestroy {
    private static nextId = 0;

    @Input() active = true;
    @Input() duration = 1000;
    @Input() autoHide = true;
    @Input() heading = 'Çalışma alanınız hazırlanıyor';
    @Input() subtitle = 'Projeleriniz ve uygulamalarınız birazdan hazır olacak.';
    @Input() badgeText = 'Çalışma alanı başlatılıyor';
    @Input() readyText = 'Çalışma alanınız hazır';
    @Input() progressLabel = 'Yükleme ilerlemesi';

    // Bu event sadece animasyonun tamamlandığını bildirir; API sonucunu bildirmez.
    @Output() completed = new EventEmitter<void>();

    readonly headingId = `one-loader-heading-${++DashboardLoader.nextId}`;
    readonly descriptionId = `${this.headingId}-description`;
    readonly monitorPath =
        'M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm5 13v4m6-4v4m-8 0h10';

    @Input() steps: DashboardLoadingStep[] = [
        {
            title: 'Hesabınıza erişiliyor',
            activeText: 'Hesabınız doğrulanıyor',
            completedText: 'Hesap doğrulandı',
            iconPath: 'M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6l-8-3Zm-4 9 3 3 5-6'
        },
        {
            title: 'Profiliniz yükleniyor',
            activeText: 'Profil bilgileriniz yükleniyor',
            completedText: 'Profil hazır',
            iconPath: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2H4Z'
        },
        {
            title: 'Projeleriniz getiriliyor',
            activeText: 'Proje bilgileri yükleniyor',
            completedText: 'Projeler hazır',
            iconPath: 'M3 7V5a1 1 0 0 1 1-1h5l3 3h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7Z'
        },
        {
            title: 'Uygulamalarınız hazırlanıyor',
            activeText: 'Uygulamalarınız yükleniyor',
            completedText: 'Uygulamalar hazır',
            iconPath: 'M3 3h7v7H3V3Zm11 0h7v7h-7V3ZM3 14h7v7H3v-7Zm11 0h7v7h-7v-7Z'
        },
        {
            title: 'Dashboard başlatılıyor',
            activeText: 'Çalışma alanınız başlatılıyor',
            completedText: 'Dashboard hazır',
            iconPath: this.monitorPath
        }
    ];

    DashboardLoader = false;
    DashboardLoaderBars: number[] = [];

    private loaderTimer: ReturnType<typeof setInterval> | null = null;
    private initialized = false;
    private destroyed = false;
    private animationComplete = false;

    constructor(private readonly changeDetector: ChangeDetectorRef) {}

    get isVisible(): boolean {
        return this.active && this.DashboardLoader;
    }

    get currentStep(): number {
        const index = this.DashboardLoaderBars.findIndex(value => value < 100);

        return index === -1 ? this.steps.length : index;
    }

    get isComplete(): boolean {
        return this.animationComplete;
    }

    get percentage(): number {
        if (this.steps.length === 0) {
            return this.animationComplete ? 100 : 0;
        }

        const total = this.DashboardLoaderBars.reduce((sum, value) => sum + value, 0);

        return Math.floor(total / this.steps.length);
    }

    get currentTitle(): string {
        return this.isComplete
            ? this.readyText
            : this.steps[this.currentStep]?.title ?? this.heading;
    }

    ngOnInit(): void {
        this.initialized = true;

        if (this.active) {
            this.StartDashboardLoader();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized || this.destroyed) {
            return;
        }

        if (!this.active) {
            this.StopTimer();
            this.DashboardLoader = false;
            this.changeDetector.markForCheck();
            return;
        }

        if (changes['active'] || changes['duration'] || changes['steps']) {
            this.StartDashboardLoader();
        } else if (changes['autoHide'] && this.animationComplete) {
            this.DashboardLoader = !this.autoHide;
            this.changeDetector.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.StopTimer();
    }

    StartDashboardLoader(): void {
        this.StopTimer();

        if (this.destroyed || !this.active) {
            return;
        }

        this.animationComplete = false;
        this.DashboardLoader = true;
        this.UpdateBars(0);

        const totalDuration = Number.isFinite(this.duration)
            ? Math.max(1, this.duration)
            : 1000;
        const startedAt = performance.now();

        this.loaderTimer = setInterval(() => {
            if (this.destroyed || !this.active) {
                this.StopTimer();
                return;
            }

            const elapsed = performance.now() - startedAt;
            const fraction = Math.min(1, elapsed / totalDuration);

            this.UpdateBars(fraction);

            if (fraction >= 1) {
                this.StopTimer();
                this.animationComplete = true;
                this.DashboardLoader = !this.autoHide;
                this.changeDetector.markForCheck();
                this.completed.emit();
                return;
            }

            this.changeDetector.markForCheck();
        }, 16);

        this.changeDetector.markForCheck();
    }

    private UpdateBars(fraction: number): void {
        const count = this.steps.length;

        if (fraction >= 1) {
            this.DashboardLoaderBars = this.steps.map(() => 100);
            return;
        }

        this.DashboardLoaderBars = this.steps.map((_, index) => {
            const firstDuration = count === 1 ? 1 : 0.3;
            const remainingDuration = count > 1 ? 0.7 / (count - 1) : 1;
            const start = index === 0 ? 0 : firstDuration + (index - 1) * remainingDuration;
            const duration = index === 0 ? firstDuration : remainingDuration;
            const progress = ((fraction - start) / duration) * 100;

            return Math.max(0, Math.min(100, progress));
        });
    }

    private StopTimer(): void {
        if (this.loaderTimer !== null) {
            clearInterval(this.loaderTimer);
            this.loaderTimer = null;
        }
    }

    GetStepProgress(index: number): number {
        return this.DashboardLoaderBars[index] ?? 0;
    }

    GetStepText(index: number): string {
        const step = this.steps[index];

        if (!step) {
            return '';
        }

        if (this.GetStepProgress(index) >= 100) {
            return step.completedText;
        }

        return index === this.currentStep ? step.activeText : 'Bekliyor';
    }
}
